import * as signalR from "@microsoft/signalr";
import { BehaviorSubject, Subject } from "rxjs";
import { USER_ROLES } from "../constants/user-roles";
import { getI18nLanguageFromLocalStorage } from "../helpers/localStorageHelper";
import { AuthenticationService } from "./authService";

// Shared state
let hubConnection = null;
let connected = false;
const onNotificationsUpdated$ = new BehaviorSubject([]);
const onListNotificationsUpdated$ = new BehaviorSubject([]);

let shoppingCartHubConnection = null;
let shoppingCartConnected = false;
const onShoppingCartUpdated$ = new Subject(); // Observable for notification updates

// Getter for hubConnection
export const getHubConnection = () => hubConnection;
export const getShoppingCartHubConnection = () => shoppingCartHubConnection;

// Initialize SignalR connection of Notification
export const initializeConnection = async (baseURL) => {
  const currentUser = AuthenticationService.instance().getCurrentUser();

  if (hubConnection?.state === signalR.HubConnectionState.Connecting) {
    while (hubConnection.state === signalR.HubConnectionState.Connecting) {
      await new Promise((resolve) => setTimeout(resolve, 300)); // Wait for 300ms
      if (hubConnection.state !== signalR.HubConnectionState.Connecting) {
        return;
      }
    }
    return;
  }

  hubConnection = new signalR.HubConnectionBuilder()
    .withUrl(`${baseURL}/api/hub/notification`, {
      accessTokenFactory: () => currentUser.token ?? "",
      transport: signalR.HttpTransportType.WebSockets,
      skipNegotiation: true,
      withCredentials: false
    })
    .withAutomaticReconnect()
    .configureLogging(signalR.LogLevel.Information)
    .build();

  await hubConnection
    .start()
    .then(() => {
      connected = true;
    })
    .catch((err) => {
      console.error("SignalR start failed", err);
      connected = false;
    });

  hubConnection.on("update-notifications", async (ids) => {
    await getAllNotifications(currentUser.id, getI18nLanguageFromLocalStorage());
  });

  hubConnection.on(`notify-admin:${currentUser.email}`, (all) => {
    onNotificationsUpdated$.next(all);
    onListNotificationsUpdated$.next(all);
  });
  hubConnection.on(`notify-trainer:${currentUser.email}`, (all) => {
    onNotificationsUpdated$.next(all);
    onListNotificationsUpdated$.next(all);
  });
  hubConnection.on(`notify-student:${currentUser.email}`, (all) => {
    onNotificationsUpdated$.next(all);
    onListNotificationsUpdated$.next(all);
  });

  const roles = (currentUser.roles || "")
    .split(",")
    .map(r => r.trim().toLowerCase())
    .filter(Boolean);

  roles.forEach(role => {
    hubConnection.on(`notices-${role}`, async (_payload) => {
      await getAllNotifications(currentUser.id, getI18nLanguageFromLocalStorage());
    });
  });

  hubConnection.onreconnected(async () => {
    await getAllNotifications(currentUser.id, getI18nLanguageFromLocalStorage());
  });
};

// Initialize SignalR connection of Shopping Cart
export const initializeShoppingCartConnection = async (baseURL) => {
  const currentUser = AuthenticationService.instance().getCurrentUser();

  if (shoppingCartHubConnection?.state === signalR.HubConnectionState.Connecting) {
    while (shoppingCartHubConnection.state === signalR.HubConnectionState.Connecting) {
      await new Promise((resolve) => setTimeout(resolve, 300)); // Wait for 300ms
      if (shoppingCartHubConnection.state !== signalR.HubConnectionState.Connecting) {
        return;
      }
    }
    return;
  }

  shoppingCartHubConnection = new signalR.HubConnectionBuilder()
    .withUrl(`${baseURL}/api/hub/shopping-cart`, {
      accessTokenFactory: () => currentUser?.token ?? "",
      transport: signalR.HttpTransportType.WebSockets,
      skipNegotiation: true,
      withCredentials: false
    })
    .withAutomaticReconnect()
    .configureLogging(signalR.LogLevel.Information)
    .build();

  await shoppingCartHubConnection
    .start()
    .then(() => {
      shoppingCartConnected = true;
    })
    .catch((err) => {
      shoppingCartConnected = false;
    });
};

// Ensure the connection is established
const ensureConnection = async () => {
  if (connected) return; // Connection is already established
  await initializeConnection();
  if (!connected) {
    throw new Error("SignalR connection failed");
  }
};

const ensureShoppingCartConnection = async () => {
  if (shoppingCartConnected) return; // Connection is already established
  await initializeShoppingCartConnection();
  if (!shoppingCartConnected) {
    throw new Error("SignalR connection failed");
  }
};

export const getNotifications = async (userId, roles) => {
  await ensureConnection();

  const notifications = []; // Array to store notifications for all roles
  if (Array.isArray(roles) && roles.length > 0) {
    for (const role of roles) {
      let roleNotifications = [];
      try {
        if (role === USER_ROLES.ADMIN) {
          roleNotifications = await hubConnection.invoke("GetNotificationsForAdmin", userId);
        } else if (role === USER_ROLES.TRAINER) {
          roleNotifications = await hubConnection.invoke("GetNotificationForTrainerByUser", userId);
        } else if (role === USER_ROLES.STUDENT) {
          roleNotifications = await hubConnection.invoke("GetNotificationForStudentByUser", userId);
        }

        // Combine the role-specific notifications into the main array
        notifications.push(...roleNotifications);
      } catch (error) {
        //console.error(`Error fetching notifications for role: ${role}`, error);
      }
    }
  }

  onNotificationsUpdated$.next(notifications); // Emit the fetched notifications
  onListNotificationsUpdated$.next(notifications); // Emit the fetched notifications to list observable
};

// not use in anywhere
export const getNotificationList = async (userId, roles) => {
  await ensureConnection();

  const notifications = []; // Array to store notifications for all roles
  if (Array.isArray(roles) && roles.length > 0) {
    for (const role of roles) {
      let roleNotifications = [];
      try {
        if (role === USER_ROLES.ADMIN) {
          roleNotifications = await hubConnection.invoke("GetNotificationsForAdmin", userId);
        } else if (role === USER_ROLES.TRAINER) {
          roleNotifications = await hubConnection.invoke("GetNotificationForTrainerByUser", userId);
        } else if (role === USER_ROLES.STUDENT) {
          roleNotifications = await hubConnection.invoke("GetNotificationForStudentByUser", userId);
        }

        // Combine the role-specific notifications into the main array
        notifications.push(...roleNotifications);
      } catch (error) {
        //console.error(`Error fetching notifications for role: ${role}`, error);
      }
    }
  }

  onNotificationsUpdated$.next(notifications); // Emit the fetched notifications
  onListNotificationsUpdated$.next(notifications); // Emit the fetched notifications to list observable
};

export const getShoppingCart = async () => {
  await ensureShoppingCartConnection();
  let shoppingCart = null;
  try {
    shoppingCart = await shoppingCartHubConnection.invoke("GetShoppingCart");
  } catch (error) {
    //console.error("Error fetching shopping cart items", error);
  }
  onShoppingCartUpdated$.next(shoppingCart); // Emit the fetched shopping cart items
}

export const updateNotification = async (notification) => {
  await ensureConnection();
  await hubConnection.invoke("UpdateNotification", notification);
}

export const markNotificationsRead = (ids) => {
  const curr = onNotificationsUpdated$.getValue() || [];
  onNotificationsUpdated$.next(
    curr.map(n => ids.includes(n.id) ? { ...n, status: 1 } : n)
  );
  onListNotificationsUpdated$.next(
    curr.map(n => ids.includes(n.id) ? { ...n, status: 1 } : n)
  );
};

export const removeShoppingCartItem = async (itemId) => {
  await ensureShoppingCartConnection();
  return await shoppingCartHubConnection.invoke("RemoveShoppingCartItem", itemId);
}

export const clearShoppingCart = async () => {
  await ensureShoppingCartConnection();
  return await shoppingCartHubConnection.invoke("ClearShoppingCart");
}

export const stopConnection = async () => {
  if (hubConnection) {
    await hubConnection.stop().then(() => {
      connected = false;
      console.info("SignalR connection stopped.");
    });
  }
}

export const stopShoppingCartConnection = async () => {
  if (shoppingCartHubConnection) {
    await shoppingCartHubConnection.stop().then(() => {
      connected = false;
    });
  }
}

export const getAllNotifications = async (userId, language, isGetAllNotifyBefore = false) => {
  await ensureConnection()
  try {
    const all = await hubConnection.invoke(
      "GetAllNotifications",
      userId,
      language,
      isGetAllNotifyBefore
    )
    if (isGetAllNotifyBefore) {
      onListNotificationsUpdated$.next(all);
    } else {

      onNotificationsUpdated$.next(all);
    }
  } catch (e) { }
}


// Export the observable for subscribers
export const onNotificationsUpdated = onNotificationsUpdated$;
export const onListNotificationsUpdated = onListNotificationsUpdated$;
export const onShoppingCartUpdated = onShoppingCartUpdated$;

// --- Payment Return Hub ---
let paymentReturnHubConnection = null;
let paymentReturnConnected = false;

export const getPaymentReturnHubConnection = () => paymentReturnHubConnection;

export const initializePaymentReturnConnection = async (baseURL) => {
  const currentUser = AuthenticationService.instance().getCurrentUser();

  if (paymentReturnHubConnection?.state === signalR.HubConnectionState.Connecting) {
    while (paymentReturnHubConnection.state === signalR.HubConnectionState.Connecting) {
      await new Promise((resolve) => setTimeout(resolve, 300));
      if (paymentReturnHubConnection.state !== signalR.HubConnectionState.Connecting) {
        return;
      }
    }
    return;
  }

  paymentReturnHubConnection = new signalR.HubConnectionBuilder()
    .withUrl(`${baseURL}/api/hub/payment-return`, {
      accessTokenFactory: () => currentUser.token ?? "",
      transport: signalR.HttpTransportType.WebSockets,
      skipNegotiation: true,
      withCredentials: false
    })
    .withAutomaticReconnect()
    .configureLogging(signalR.LogLevel.Information)
    .build();

  await paymentReturnHubConnection
    .start()
    .then(() => {
      paymentReturnConnected = true;
    })
    .catch((err) => {
      paymentReturnConnected = false;
    });
};

export const stopPaymentReturnConnection = async () => {
  if (paymentReturnHubConnection) {
    await paymentReturnHubConnection.stop();
    paymentReturnHubConnection = null;
    paymentReturnConnected = false;
  }
};

export const clearAllNotifications = async (language) => {
  await ensureConnection();
  try {
    const ids = await hubConnection.invoke("MarkAllAsRead", language);
    markNotificationsRead(Array.isArray(ids) ? ids : []);
    const currentUser = AuthenticationService.instance().getCurrentUser();
    await getAllNotifications(currentUser.id, getI18nLanguageFromLocalStorage());
  } catch (e) {
    console.warn("Failed to clear notifications", e);
  }
};

